<template>
  <div
    class="relative input-field-text w-full md:max-w-[41.5rem]"
    :class="width"
  >
    <label
      v-if="label"
      class="text-text-secondary"
      :class="[
        ` absolute
          top-0
          left-8
          px-8
          bg-surface-primary
          w-fit
          text-14
          leading-22`,
        disabled && 'bg-surface-disabled text-text-disabled',
      ]"
      :for="inputId"
      :aria-label="ariaLabel ? ariaLabel : undefined"
    >
      {{ label }}
    </label>
    <input
      :id="inputId"
      v-model="model"
      :style="`${specificLeftOffsetRemForText ? 'padding-left:' + specificLeftOffsetRemForText + 'rem;' : ''}`"
      :class="[
        ` peer
          inline-block
          enabled:cursor-pointer
          w-full
          border-border-primary
          border-solid
          border-md
          rounded-xl
          h-56
          px-16
          mt-11
          mb-8
          shadow-grey-500
          shadow-[inset_1px_1px_0_1px]
          bg-surface-primary 
          text-18
          leading-20
          halebop-focus-rounded
          text-text-primary
          placeholder:text-text-secondary
          ${icon ? 'pl-52' : ''}`,
        disabled &&
          ` !text-text-disabled
            border-grey-200
            border-solid
            border-2
            bg-surface-disabled
            shadow-grey-200`,
      ]"
      :type="type"
      :placeholder
      :name
      :disabled
      :aria-disabled="disabled"
      :aria-invalid="errorMessage ? 'true' : undefined"
      :blur="$emit('blur')"
      :autocomplete="autocomplete ? 'on' : 'off'"
      v-on="handlers"
      @input="onInput($event)"
    />
    <nuxt-icon
      v-if="icon && !model"
      class="absolute left-12 top-26 text-24 text-icon-secondary"
      :name="icon"
      aria-hidden="true"
    />
    <template
      v-if="
        !clearable &&
        ((errorMessage && autoShowErrorMsg) ||
          (validate && meta.validated && meta.valid))
      "
    >
      <nuxt-icon
        class="absolute right-12 top-26 text-24"
        filled
        :name="errorMessage ? 'alert-circle-red' : 'check-circle-green'"
        aria-hidden="true"
      />
    </template>
    <template v-if="clearable && model">
      <button
        :class="[
          ` absolute
            right-12
            top-[28px]
            rounded-xl
            text-text-primary
            `,
        ]"
        type="reset"
        aria-label="töm inputfältet"
        @click="clearInput"
      >
        <nuxt-icon class="text-24" name="x" aria-hidden="true" />
      </button>
    </template>
    <span
      :class="[
        successful && 'bg-border-success !opacity-100',
        errorMessage && autoShowErrorMsg && 'bg-border-error !opacity-100',
        ` absolute
          bg-blue-500
          h-4
          left-4
          right-2
          top-[6.1rem]
          border-solid
          border-b-12
          border-l-12
          rounded-bl-xl
          rounded-br-xl
          transition-all
          duration-300
          ease-in
          opacity-0
          peer-hover:opacity-100
          peer-hover:peer-disabled:opacity-0`,
      ]"
      aria-hidden="true"
    />
    <slot name="selectionContainer"></slot>
    <span aria-live="polite">
      <p
        v-if="errorMessage && autoShowErrorMsg"
        :aria-describedby="inputId"
        class="text-text-error heading-xxs px-16 py-8 min-h-28 transition-height duration-300 ease-in-out"
      >
        {{ errorMessage }}
      </p>
    </span>

    <!-- todo: jag tog bort min-h-29 här, måste ju ha följder!! Vi har min-h-28 ovan, jesus... -->
    <p
      v-if="!errorMessage && helpText"
      :class="`
        text-text-secondary
        heading-xxs
        invisible
        transition-height 
        duration-300
        ease-in-out 
        h-0
        peer-focus:h-36
        peer-focus:px-16
        peer-focus:py-8
        peer-focus:visible
      `"
    >
      {{ helpText }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { modes, type InteractionMode } from '../interactionModes';
import type { FormFieldValidatorKeys } from '~/plugins/validator';
const { $validator } = useNuxtApp();

interface Props {
  type?: string;
  name: string;
  label: string;
  ariaLabel?: string;
  validate?: FormFieldValidatorKeys | FormFieldValidatorKeys[];
  inputId?: string;
  initialValue?: string;
  placeholder?: string;
  width?: string;
  mode?: keyof InteractionMode;
  disabled?: boolean;
  autoShowErrorMsg?: boolean;
  helpText?: string;
  icon?: string;
  specificLeftOffsetRemForText?: number;
  clearable?: boolean;
  autocomplete?: boolean;
  inputDebounceMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  name: '',
  label: '',
  ariaLabel: undefined,
  inputId: '',
  initialValue: '',
  validate: undefined,
  placeholder: '',
  width: '',
  mode: 'aggressive',
  startValue: '',
  disabled: false,
  autoShowErrorMsg: true,
  helpText: undefined,
  icon: undefined,
  specificLeftOffsetRemForText: 0,
  clearable: true,
  autocomplete: true,
  inputDebounceMs: 0,
});

const emit = defineEmits(['cleared', 'blur', 'isValid', 'input']);

function clearInput() {
  resetField();
  model.value = '';
  emit('cleared', model.value);
}

const debouncedEmitInput = useDebounceFn(
  (event: Event) => emit('input', event),
  props.inputDebounceMs,
);

async function onInput(event: Event) {
  setErrors([]);
  if (props.inputDebounceMs > 0) {
    await debouncedEmitInput(event);
  } else {
    emit('input', event);
  }
}

const validator = computed(() => {
  if (props.validate && Array.isArray(props.validate)) {
    return props.validate.map((v) => $validator[v]);
  }

  if (props.validate) {
    return $validator[props.validate];
  }

  return undefined;
});

const { meta, errorMessage, handleChange, handleBlur, resetField, setErrors } =
  useField(toRef(props, 'name'), validator.value, {
    validateOnValueUpdate: false,
    initialValue: props.initialValue,
  });

const successful = ref(false);
watch(meta, () => {
  successful.value =
    !!props.validate && meta.validated && meta.valid && !errorMessage.value;

  emit('isValid', meta.valid);
});

const handlers = computed(() => {
  const on: any = {
    blur: handleBlur,
    input: [(e: unknown) => handleChange(e, false)],
  };

  const triggers = modes[props.mode]({
    errorMessage,
    meta,
  });

  triggers.forEach((t: string | number) => {
    if (Array.isArray(on[t])) {
      on[t].push(handleChange);
    } else {
      on[t] = handleChange;
    }
  });

  return on;
});

const model = defineModel<string>();

onMounted(() => {
  if (props.initialValue && !model.value) {
    model.value = props.initialValue;
  }
});
</script>

<style scoped lang="scss"></style>
